import React, { useState } from 'react';
import { Loading } from '@cotera/client/app/components';
import { toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Textarea } from '@cotera/client/v0/components/ui/textarea';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { emitLocalDatasetImport } from './dataset-import';

/**
 * The SELECT a warehouse dataset is seeded from, editable, with a button to run
 * it again. Prefilled with the stored query so the user edits rather than
 * retypes. Re-seeding the same dataset (rather than making a new one) keeps every
 * trigger and binding pointing at it, and re-runs the whole seed — the
 * re-population and setup a fresh import does.
 *
 * Used two ways: under a failed seed (retry the query that failed) and as the
 * "Edit source" action on a healthy dataset (change what it reads). The copy and
 * a `onSubmitted` hook to close a surrounding dialog are what differ.
 */
export const WarehouseReseedForm: React.FC<{
  datasetId: string;
  initialQuery: string;
  title?: string;
  submitLabel?: string;
  onSubmitted?: () => void;
}> = ({
  datasetId,
  initialQuery,
  title = 'Edit the query and try again',
  submitLabel = 'Run query',
  onSubmitted,
}) => {
  const client = useTenantedClient();
  const [sourceQuery, setSourceQuery] = useState(initialQuery);
  const [saving, setSaving] = useState(false);
  const canSubmit = sourceQuery.trim() !== '';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || saving) {
      return;
    }
    setSaving(true);
    const result = await client.resource.dataset.reseedWarehouse({
      id: datasetId,
      sourceQuery: sourceQuery.trim(),
    });
    setSaving(false);
    if (result.isErr()) {
      toast.error(result.error.err.message ?? 'Could not start the query.');
      return;
    }
    // Flip the page to progress immediately, without waiting for the first
    // server broadcast — this also clears the failed panel that is showing.
    emitLocalDatasetImport(datasetId, { phase: 'staged' });
    onSubmitted?.();
  };

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2 rounded-md border bg-muted/30 px-4 py-3"
    >
      <span className="text-sm font-medium text-foreground">{title}</span>
      <Textarea
        value={sourceQuery}
        rows={6}
        spellCheck={false}
        className="font-mono text-xs"
        placeholder="select * from analytics.orders"
        onChange={(e) => setSourceQuery(e.target.value)}
      />
      <div className="flex justify-end">
        <Button type="submit" disabled={!canSubmit || saving}>
          {saving ? <Loading.Spinner variant="sm" /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
